import { v4 as uuid } from 'uuid'
import {
  IconKind,
  VisualShape,
  VisualZoom,
} from 'app/frontend/types/custom/VisualPlan'
import {
  DEFAULT_FILL_COLOR,
  DEFAULT_ENCOUNTER_ICON_SIZE,
  DEFAULT_ICON_SIZE,
  DEFAULT_ICON_STYLE,
  DEFAULT_ICON_STROKE_COLOR,
  DEFAULT_ICON_STROKE_WIDTH,
  DEFAULT_POINTER_SIZE,
  DEFAULT_STROKE_COLOR,
  DEFAULT_STROKE_WIDTH,
  DEFAULT_TEXT_BACKGROUND,
  DEFAULT_TEXT_COLOR,
  DEFAULT_TEXT_FONT_SIZE,
  DEFAULT_TEXT_WIDTH,
  IconStyle,
  ShapeStyle,
} from '../constants'
import { getIconCanvasFrame } from '../iconKinds'

type Dims = {
  width: number
  height: number
  aspect: number
}

type GeometryType = Exclude<VisualShape['type'], 'icon' | 'text'>

type Position = { x: number; y: number }

const viewportCenter = (zoom: VisualZoom | undefined) => {
  if (!zoom || zoom.scale <= 1) return { x: 0.5, y: 0.5 }
  const size = 1 / zoom.scale
  return {
    x: zoom.x * (1 - size) + size / 2,
    y: zoom.y * (1 - size) + size / 2,
  }
}

export const createShape = (
  type: GeometryType,
  dims: Dims,
  style?: ShapeStyle,
  zoom?: VisualZoom,
  position?: Position,
): VisualShape => {
  const { width, height, aspect } = dims
  const scale = zoom && zoom.scale > 1 ? zoom.scale : 1
  const fillRgb = style?.fill_rgb ?? DEFAULT_FILL_COLOR
  const strokeRgb = style?.stroke_rgb ?? DEFAULT_STROKE_COLOR
  const strokeWidth = (style?.stroke_width ?? DEFAULT_STROKE_WIDTH) / scale
  const center = position ?? viewportCenter(zoom)
  const base = {
    id: uuid(),
    x: center.x,
    y: center.y,
    fill_rgb: fillRgb,
    stroke_rgb: strokeRgb,
    stroke_width: strokeWidth,
  }
  const squareSide = 0.2 / scale
  const squareHeight = aspect > 0 ? (squareSide * width) / height : squareSide
  const triangleHeight =
    aspect > 0 ? (squareSide * width * Math.sqrt(3)) / 2 / height : squareSide

  if (type === 'rect') {
    return { ...base, type: 'rect', width: squareSide, height: squareHeight }
  }
  if (type === 'circle') {
    return {
      ...base,
      type: 'circle',
      radius_x: 0.08 / scale,
      radius_y: 0.08 / scale,
    }
  }
  if (type === 'triangle') {
    return {
      ...base,
      type: 'triangle',
      width: squareSide,
      height: triangleHeight,
    }
  }
  if (type === 'pencil') {
    return { ...base, type: 'pencil', points: [0, 0, 0, 0] }
  }
  if (type === 'line') {
    return {
      ...base,
      type: 'line',
      points: [-0.1 / scale, 0, 0.1 / scale, 0],
    }
  }
  return {
    ...base,
    type: 'arrow',
    points: [-0.1 / scale, 0, 0.1 / scale, 0],
    pointer_length: DEFAULT_POINTER_SIZE / scale,
    pointer_width: DEFAULT_POINTER_SIZE / scale,
    fill_rgb: strokeRgb,
  }
}

export const createText = (
  zoom?: VisualZoom,
  position?: Position,
): Extract<VisualShape, { type: 'text' }> => {
  const scale = zoom && zoom.scale > 1 ? zoom.scale : 1
  const center = position ?? viewportCenter(zoom)
  const fontSize = DEFAULT_TEXT_FONT_SIZE / scale
  const textWidth = DEFAULT_TEXT_WIDTH / scale

  return {
    id: uuid(),
    x: center.x,
    y: center.y,
    type: 'text',
    text: 'Text',
    width: textWidth,
    font_size: fontSize,
    fill_rgb: DEFAULT_TEXT_COLOR,
    stroke_rgb: DEFAULT_TEXT_BACKGROUND,
  }
}

export const createIcon = (
  kind: IconKind,
  value: string,
  dims: Dims,
  style?: IconStyle,
  zoom?: VisualZoom,
  position?: Position,
): Extract<VisualShape, { type: 'icon' }> => {
  const { width, height, aspect } = dims
  const scale = zoom && zoom.scale > 1 ? zoom.scale : 1
  const center = position ?? viewportCenter(zoom)
  const iconStyle = style ?? DEFAULT_ICON_STYLE
  const defaultSize =
    kind === 'encounter' ? DEFAULT_ENCOUNTER_ICON_SIZE : DEFAULT_ICON_SIZE
  const size =
    iconStyle.size === DEFAULT_ICON_SIZE ? defaultSize : iconStyle.size
  const squareSide = size / scale
  const squareHeight = aspect > 0 ? (squareSide * width) / height : squareSide
  const frame = getIconCanvasFrame(kind, value)

  return {
    id: uuid(),
    x: center.x,
    y: center.y,
    type: 'icon',
    kind,
    value,
    width: squareSide,
    height: squareHeight,
    opacity: iconStyle.opacity,
    ...(kind === 'encounter' || kind === 'ability'
      ? {}
      : { entity_count: iconStyle.entity_count ?? 0 }),
    ...(frame
      ? {
          stroke_rgb:
            kind === 'encounter' &&
            (iconStyle.stroke_rgb === undefined ||
              iconStyle.stroke_rgb === DEFAULT_ICON_STROKE_COLOR)
              ? DEFAULT_STROKE_COLOR
              : iconStyle.stroke_rgb ?? DEFAULT_ICON_STROKE_COLOR,
          stroke_width:
            (iconStyle.stroke_width ?? DEFAULT_ICON_STROKE_WIDTH) / scale,
        }
      : {}),
  }
}
